// The whole venue, seen from the crowd (diagram 2 of docs/plans/ext-teams-seating.md, flipped so the
// crowd is at the bottom: the screen's left is the crowd's left). The two teams sit along a Λ that
// opens toward the crowd. Venue seats: left 1–5 (1 outermost, 5 at the apex), right 6–10 (6 at the
// apex, 10 outermost). Each side's `seats` array is that team's 5 players in the crowd's
// left-to-right order, so left position k = seat k and right position k = seat 5 + k.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { S, SeatUnit, Crowd, SeatCard, SubsBox, swapSeat } from './StageArt';

const W = 1200, H = 710;
const APEX = { x: 600, y: 110 };
const FOOT = { x: 330, y: 510 };            // left leg's outer end (the right leg mirrors it)
const LEG = { x: (APEX.x - FOOT.x), y: (APEX.y - FOOT.y) };
const LEN = Math.hypot(LEG.x, LEG.y);
const OUT = { x: LEG.y / LEN, y: -LEG.x / LEN };  // outward normal of the left leg (up-left)
const ANGLE = Math.atan2(-OUT.x, OUT.y) * 180 / Math.PI; // SeatUnit faces -y → turn it to face inward

const mirror = (p) => ({ x: W - p.x, y: p.y });
/** Point on the left leg for crowd position k (1 = outermost) at `off` px outside the leg. */
function leftPoint(k, off) {
  const tt = 0.12 + 0.18 * (k - 1);
  return { x: FOOT.x + LEG.x * tt + OUT.x * off, y: FOOT.y + LEG.y * tt + OUT.y * off };
}
/** Right side: crowd position k = 1 is at the apex, so it mirrors left position 6 - k. */
const seatPoint = (side, k, off) => (side === 'left' ? leftPoint(k, off) : mirror(leftPoint(6 - k, off)));

function platformPoints(off) {
  const d = off, apexY = APEX.y - d * (Math.abs(OUT.y) + (OUT.x * OUT.x) / Math.abs(OUT.y));
  const l = { x: FOOT.x + OUT.x * d - LEG.x * 0.06, y: FOOT.y + OUT.y * d - LEG.y * 0.06 };
  const r = mirror(l);
  return `${l.x},${l.y} ${APEX.x},${apexY} ${r.x},${r.y}`;
}

function Side({ side, data, onChange, readOnly }) {
  const { t } = useTranslation();
  const { tint, players, seats } = data;
  const options = players.map(p => {
    const at = seats.indexOf(p.steam_id);
    return { value: p.steam_id, label: p.ign, sub: at >= 0 ? null : t('extSeating.subShort', 'sub') };
  });
  return (
    <g>
      {[1, 2, 3, 4, 5].map(k => {
        const p = seatPoint(side, k, 30);
        return <SeatUnit key={k} x={p.x} y={p.y} angle={side === 'left' ? ANGLE : -ANGLE} tint={tint} filled={!!seats[k - 1]} />;
      })}
      {[1, 2, 3, 4, 5].map(k => {
        const c = seatPoint(side, k, 125);
        return (
          <foreignObject key={`c${k}`} x={c.x - 75} y={c.y - 34} width={150} height={70}>
            <SeatCard n={side === 'left' ? k : 5 + k} tint={tint} value={seats[k - 1]} options={options} readOnly={readOnly}
              emptyLabel={t('extSeating.emptySeat', 'Pick a player')} onChange={(sid) => onChange(swapSeat(seats, k - 1, sid))} />
          </foreignObject>
        );
      })}
    </g>
  );
}

export default function VenueStage({ left, right, onChange, readOnly }) {
  const { t } = useTranslation();
  const subsOf = (d) => d.players.filter(p => !d.seats.includes(p.steam_id));
  const footL = seatPoint('left', 1, 0), footR = seatPoint('right', 5, 0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
      aria-label={t('extSeating.venueAria', 'Venue seating seen from the crowd: {{left}} on the left, {{right}} on the right', { left: left.name, right: right.name })}>
      {/* stage platform (Λ) + centre line of each row of desks */}
      <polyline points={platformPoints(30)} fill="none" stroke={S.line2} strokeWidth="114" strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={platformPoints(30)} fill="none" stroke={S.card} strokeWidth="110" strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={`${FOOT.x},${FOOT.y} ${APEX.x},${APEX.y} ${W - FOOT.x},${FOOT.y}`} fill="none" stroke={S.line3} strokeWidth="1.5" strokeDasharray="2 6" />
      <Side side="left" data={left} onChange={(s) => onChange('left', s)} readOnly={readOnly} />
      <Side side="right" data={right} onChange={(s) => onChange('right', s)} readOnly={readOnly} />
      {/* team names at the outer ends, nearest the crowd */}
      <text x={footL.x - 10} y={footL.y + 86} textAnchor="middle" fontSize="19" fontWeight="800" fill={left.tint} style={{ fontFamily: "var(--font-display, 'Cabinet Grotesk', sans-serif)" }}>{left.name}</text>
      <text x={footR.x + 10} y={footR.y + 86} textAnchor="middle" fontSize="19" fontWeight="800" fill={right.tint} style={{ fontFamily: "var(--font-display, 'Cabinet Grotesk', sans-serif)" }}>{right.name}</text>
      <text x={footL.x - 10} y={footL.y + 104} textAnchor="middle" fontSize="10" fontWeight="700" letterSpacing="0.08em" fill={S.muted} style={{ textTransform: 'uppercase' }}>{t('extSeating.leftSide', 'Left side')}</text>
      <text x={footR.x + 10} y={footR.y + 104} textAnchor="middle" fontSize="10" fontWeight="700" letterSpacing="0.08em" fill={S.muted} style={{ textTransform: 'uppercase' }}>{t('extSeating.rightSide', 'Right side')}</text>
      <Crowd x={420} y={612} w={360} h={84} label={t('extSeating.crowd', 'Crowd')} />
      <foreignObject x={16} y={210} width={180} height={240}>
        <SubsBox title={t('extSeating.substitutesOf', 'Substitutes · {{team}}', { team: left.name })} tint={left.tint} players={subsOf(left)} emptyLabel={t('extSeating.noSubs', 'None')} />
      </foreignObject>
      <foreignObject x={W - 196} y={210} width={180} height={240}>
        <SubsBox title={t('extSeating.substitutesOf', 'Substitutes · {{team}}', { team: right.name })} tint={right.tint} players={subsOf(right)} emptyLabel={t('extSeating.noSubs', 'None')} />
      </foreignObject>
    </svg>
  );
}
